import { useEffect, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import CountrySelect from '../components/CountrySelect';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const STATUS_STYLES = {
  OPEN: 'bg-emerald-50 text-emerald-700',
  PENDING_APPROVAL: 'bg-amber-50 text-amber-700',
  CLOSED: 'bg-[#e0e3e5] text-[#44474f]',
  DRAFT: 'bg-[#e0e3e5] text-[#44474f]',
};

const emptyForm = {
  title: '', country: '', type: 'STUDY', feeFromAmount: '', deadline: '', description: '', perks: [''], sendNewsletter: true,
};

export default function AdminOpeningsPage() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('adminToken');

  const fetchOpportunities = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    fetch(`${API_BASE}/admin/opportunities?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => setOpportunities(json.success ? json.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(fetchOpportunities, [search]);

  const updatePerk = (i, value) => setForm((f) => { const list = [...f.perks]; list[i] = value; return { ...f, perks: list }; });
  const addPerk = () => setForm((f) => ({ ...f, perks: [...f.perks, ''] }));
  const removePerk = (i) => setForm((f) => ({ ...f, perks: f.perks.filter((_, idx) => idx !== i) }));

  useEffect(() => {
    if (!coverImage) return;
    const url = URL.createObjectURL(coverImage);
    setCoverPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [coverImage]);

  const openCreate = () => {
    setEditingId(null); setForm(emptyForm); setCoverImage(null); setCoverPreview(null); setDrawerOpen(true);
  };

  const openEdit = (op) => {
    setEditingId(op.id);
    setForm({ title: op.title || '', country: op.country || '', type: op.type || 'STUDY', feeFromAmount: op.feeFromAmount || '', deadline: op.deadline ? op.deadline.slice(0, 10) : '', description: op.description || '', perks: op.perks?.length ? op.perks : [''], sendNewsletter: false });
    setCoverImage(null); setCoverPreview(op.imageUrl || null); setDrawerOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('country', form.country);
    fd.append('type', form.type);
    fd.append('feeFromAmount', form.feeFromAmount);
    fd.append('deadline', form.deadline);
    fd.append('description', form.description);
    fd.append('perks', JSON.stringify(form.perks.filter(Boolean)));
    fd.append('sendNewsletter', form.sendNewsletter ? 'true' : 'false');
    if (coverImage) fd.append('image', coverImage);
    await fetch(`${API_BASE}/admin/opportunities${editingId ? `/${editingId}` : ''}`, {
      method: editingId ? 'PATCH' : 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    setSaving(false);
    setDrawerOpen(false);
    setForm(emptyForm); setCoverImage(null); setCoverPreview(null); setEditingId(null);
    fetchOpportunities();
  };

  const handleApprove = async (id) => {
    await fetch(`${API_BASE}/admin/opportunities/${id}/approve`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    fetchOpportunities();
  };

  const handleClose = async (id) => {
    await fetch(`${API_BASE}/admin/opportunities/${id}/close`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    fetchOpportunities();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this opening? Applicants will be notified and their applications preserved with original terms.')) return;
    await fetch(`${API_BASE}/admin/opportunities/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchOpportunities();
  };

  const totalOpenings = opportunities.length;
  const liveApps = opportunities.filter((o) => o.status === 'OPEN').length;
  const pendingReview = opportunities.filter((o) => o.status === 'PENDING_APPROVAL').length;
  const countries = new Set(opportunities.map((o) => o.country)).size;

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AdminSidebar />
      <main className="pt-16 md:pt-0 md:ml-64 min-h-screen p-8">
        <header className="flex justify-between items-center mb-10">
          <div>
            <h2 className="text-3xl font-bold text-[#00255a]">Openings Management</h2>
            <p className="text-[#64748b] mt-1">Manage global study and career opportunities.</p>
          </div>
          <button
            onClick={openCreate}
            className="text-white px-8 py-4 rounded-full font-semibold flex items-center gap-2 hover:scale-105 transition-all shadow-lg"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          >
            <span className="material-symbols-outlined">add_circle</span>
            Create New Opening
          </button>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard label="Total Openings" value={totalOpenings} color="text-[#00255a]" />
          <StatCard label="Live" value={liveApps} color="text-[#365da3]" />
          <StatCard label="Pending Review" value={pendingReview} color="text-[#79161e]" />
          <StatCard label="Countries" value={countries} color="text-[#00255a]" />
        </div>

        <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-[0px_10px_30px_rgba(30,60,114,0.05)] overflow-hidden">
          <div className="p-6 border-b border-[#e2e8f0] flex justify-between items-center">
            <div className="relative w-72">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b]">search</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by Title or Country..."
                className="w-full pl-10 pr-4 py-2 bg-[#f7f9fb] rounded-lg border border-[#c4c6d1] focus:ring-2 focus:ring-[#00255a]/10 outline-none"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-[#f2f4f6] border-b border-[#e2e8f0]">
                <tr>
                  {['', 'Country', 'Title', 'Type', 'Fee', 'Status', 'Posted By', ''].map((h, idx) => (
                    <th key={idx} className="px-6 py-4 text-[#44474f] uppercase text-xs tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {loading ? (
                  <tr><td colSpan="8" className="p-12 text-center text-[#64748b]">Loading...</td></tr>
                ) : opportunities.length === 0 ? (
                  <tr><td colSpan="8" className="p-12 text-center text-[#64748b]">No openings found.</td></tr>
                ) : (
                  opportunities.map((op) => (
                    <tr key={op.id} className="hover:bg-[#f7f9fb] transition-colors group">
                      <td className="px-6 py-5">
                        <div className="w-12 h-12 rounded-lg bg-[#e0e3e5] overflow-hidden flex items-center justify-center border border-[#e2e8f0]">
                          {op.imageUrl ? (
                            <img className="w-full h-full object-cover" src={op.imageUrl} alt={op.title} />
                          ) : (
                            <span className="material-symbols-outlined text-[#00255a]">image</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-5 font-medium text-[#00255a]">{op.country}</td>
                      <td className="px-6 py-5">
                        <span className="text-[#0f172a] font-semibold">{op.title}</span>
                        {op.institution && <div className="text-xs text-[#64748b]">{op.institution.name}</div>}
                      </td>
                      <td className="px-6 py-5">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${
                          op.type === 'STUDY' ? 'bg-[#365da3]/10 text-[#365da3] border-[#365da3]/20' : 'bg-[#79161e]/10 text-[#862026] border-[#79161e]/20'
                        }`}>
                          {op.type}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-sm text-[#44474f]">
                        {op.feeFromAmount ? `${op.feeCurrency} ${op.feeFromAmount}` : '—'}
                      </td>
                      <td className="px-6 py-5">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLES[op.status]}`}>
                          {op.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-sm text-[#64748b]">{op.postedBy?.fullName}</td>
                      <td className="px-6 py-5 text-right whitespace-nowrap">
                        {op.status === 'PENDING_APPROVAL' && (
                          <button onClick={() => handleApprove(op.id)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-full transition-all" title="Approve">
                            <span className="material-symbols-outlined">check_circle</span>
                          </button>
                        )}
                        {op.status === 'OPEN' && (
                          <button onClick={() => handleClose(op.id)} className="p-2 text-[#64748b] hover:bg-[#f2f4f6] rounded-full transition-all" title="Close">
                            <span className="material-symbols-outlined">block</span>
                          </button>
                        )}
                        <button onClick={() => openEdit(op)} className="p-2 text-[#64748b] hover:text-[#00255a] hover:bg-[#f2f4f6] rounded-full transition-all" title="Edit opening">
                          <span className="material-symbols-outlined">edit</span>
                        </button>
                        <button onClick={() => handleDelete(op.id)} className="p-2 text-[#64748b] hover:text-red-600 hover:bg-red-50 rounded-full transition-all" title="Delete">
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Create Drawer */}
        {drawerOpen && (
          <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setDrawerOpen(false)}>
            <div className="w-full max-w-lg bg-white flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-[#e2e8f0] flex justify-between items-center">
                <h3 className="text-xl font-bold text-[#00255a]">{editingId ? 'Edit Opening' : 'Create New Opening'}</h3>
                <button onClick={() => setDrawerOpen(false)} className="p-2 hover:bg-[#f2f4f6] rounded-full">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-8 space-y-6">
                <FormField label="Position Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><label className="block text-xs font-bold text-[#44474f] uppercase">Country</label><CountrySelect required value={form.country} onChange={(v) => setForm({ ...form, country: v })} /></div>
                  <div className="space-y-2">
                    <label className="block text-xs font-bold text-[#44474f] uppercase">Type</label>
                    <div className="flex p-1 bg-[#f2f4f6] rounded-xl border border-[#e2e8f0]">
                      {['STUDY', 'JOB'].map((t) => (
                        <button
                          key={t}
                          onClick={() => setForm({ ...form, type: t })}
                          className={`flex-1 py-2 rounded-lg text-sm font-semibold ${form.type === t ? 'bg-white shadow-sm text-[#00255a]' : 'text-[#64748b]'}`}
                        >
                          {t === 'STUDY' ? 'Study' : 'Job'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Associated Fee (USD)" type="number" value={form.feeFromAmount} onChange={(v) => setForm({ ...form, feeFromAmount: v })} />
                  <FormField label="Application Deadline" type="date" value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} />
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-bold text-[#44474f] uppercase">Full Description</label>
                  <textarea
                    rows="4"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] focus:ring-2 focus:ring-[#00255a]/10 outline-none resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-bold text-[#44474f] uppercase">Opening Picture</label>
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 rounded-xl bg-[#f2f4f6] border border-[#e2e8f0] overflow-hidden flex items-center justify-center shrink-0">
                      {coverPreview ? (
                        <img src={coverPreview} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="material-symbols-outlined text-[#64748b]">image</span>
                      )}
                    </div>
                    <input type="file" accept="image/*" onChange={(e) => setCoverImage(e.target.files[0])} className="text-sm" />
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="block text-xs font-bold text-[#44474f] uppercase">Key Perks & Benefits</label>
                  {form.perks.map((perk, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        value={perk}
                        onChange={(e) => updatePerk(i, e.target.value)}
                        placeholder="e.g. Visa Sponsorship Included"
                        className="flex-1 px-4 py-3 rounded-xl border border-[#c4c6d1] focus:ring-2 focus:ring-[#00255a]/10 outline-none text-sm"
                      />
                      <button type="button" onClick={() => removePerk(i)} className="p-2 text-red-500">
                        <span className="material-symbols-outlined text-[18px]">remove_circle</span>
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={addPerk} className="text-[#00255a] text-sm font-semibold flex items-center gap-1">
                    <span className="material-symbols-outlined text-[16px]">add</span> Add Benefit
                  </button>
                </div>
                <label className="flex items-center gap-3 p-4 bg-[#f2f4f6] rounded-xl border border-[#e2e8f0] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.sendNewsletter}
                    onChange={(e) => setForm({ ...form, sendNewsletter: e.target.checked })}
                    className="w-5 h-5 rounded text-[#00255a]"
                  />
                  <div>
                    <span className="block font-semibold text-[#191c1e]">Notify newsletter subscribers</span>
                    <span className="block text-xs text-[#64748b]">Emails everyone subscribed as soon as this opening goes live. Uncheck to publish quietly.</span>
                  </div>
                </label>
              </div>
              <div className="p-6 border-t border-[#e2e8f0] flex gap-4">
                <button onClick={() => { setDrawerOpen(false); setCoverImage(null); setCoverPreview(null); setEditingId(null); }} className="flex-1 py-4 border-2 border-[#00255a] text-[#00255a] font-semibold rounded-xl">
                  Discard
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-4 text-white font-semibold rounded-xl shadow-lg disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
                >
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Publish Opening'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white p-6 rounded-2xl border border-[#e2e8f0] shadow-sm">
      <p className="text-[#64748b] text-xs uppercase font-bold">{label}</p>
      <h3 className={`text-2xl font-bold mt-2 ${color}`}>{value}</h3>
    </div>
  );
}

function FormField({ label, value, onChange, type = 'text' }) {
  return (
    <div className="space-y-2">
      <label className="block text-xs font-bold text-[#44474f] uppercase">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] focus:ring-2 focus:ring-[#00255a]/10 outline-none"
      />
    </div>
  );
}
